import { useMemo } from "react";

import {
    calculateActivityContributions,
    LendVaultActivityItem,
    useLendVaultActivity,
    VaultActionType
} from "@bridgesplit/abf-react";
import { TokenListMetadata } from "@bridgesplit/abf-sdk";
import { DispatchType } from "@bridgesplit/react";
import {
    AddressCopy,
    Column,
    createRows,
    Pagination,
    SectionedCard,
    Table,
    TableColumn,
    Text,
    TxnSignature
} from "@bridgesplit/ui";
import { formatDate, formatToken, SortDirection, TokenAmount, toTokenAmount } from "@bridgesplit/utils";
import { useClampPaginationOnCountChange, useTableChunkedPagination } from "app/api/shared";
import { COPY } from "app/constants";

import { EventTag, EventTagKind } from "./loans";

const ACTIVITY_PAGE_SIZE = 10;
const POSITION_FETCH_SIZE = 100;
const POSITION_TABLE_PAGE_SIZE = 5;

export type VaultTransactionHistoryVariant = "activity" | "position";

interface VaultTransactionHistoryRow {
    key: string;
    txSignature: string;
    timestamp: number;
    actionType: VaultActionType;
    amount: TokenAmount;
    owner: string;
    netDeposits?: TokenAmount;
}

export function VaultTransactionHistory({
    variant,
    vaultAddress,
    metadata,
    wallet
}: {
    variant: VaultTransactionHistoryVariant;
    vaultAddress: string | undefined;
    metadata: TokenListMetadata | undefined;
    // Required for the position variant (wallet-filtered activity).
    wallet?: string;
}) {
    if (variant === "activity") return <ActivityHistory vaultAddress={vaultAddress} metadata={metadata} />;
    return <PositionHistory vaultAddress={vaultAddress} metadata={metadata} wallet={wallet} />;
}

// Vault-wide, backend-paginated (chunked over-fetch) across all signers.
function ActivityHistory({
    vaultAddress,
    metadata
}: {
    vaultAddress: string | undefined;
    metadata: TokenListMetadata | undefined;
}) {
    const pagination = useTableChunkedPagination(ACTIVITY_PAGE_SIZE);
    const activity = useLendVaultActivity(
        vaultAddress
            ? {
                  vaultAddress,
                  page: pagination.backendPage,
                  pageSize: pagination.fetchSize,
                  sortDirection: SortDirection.Desc
              }
            : undefined
    );
    const totalDataCount = activity.data?.pageInfo.totalItems;
    useClampPaginationOnCountChange(pagination.clampToCount, totalDataCount);

    const { visibleData } = pagination;
    const visibleItems = useMemo(
        () => visibleData(activity.data?.items, activity.isFetching),
        [visibleData, activity.data?.items, activity.isFetching]
    );
    const decimals = metadata?.decimals;
    const rows = useMemo(() => {
        if (!visibleItems || decimals === undefined) return [];
        return visibleItems.map((item, index) => toActivityRow(item, index, decimals));
    }, [visibleItems, decimals]);
    const columns = useMemo(() => getColumns("activity", metadata), [metadata]);
    const loading = (activity.isLoading && !activity.data) || activity.isFetching;

    return (
        <TransactionHistoryTable
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={ACTIVITY_PAGE_SIZE}
            page={pagination.page}
            setPage={pagination.setPage}
            totalDataCount={totalDataCount}
        />
    );
}

// Wallet-filtered, fetched in one window and paginated client-side. Net Deposits are the cumulative
// contribution over the fetched window (accepts the same >100-actions edge degradation as Earn).
function PositionHistory({
    vaultAddress,
    metadata,
    wallet
}: {
    vaultAddress: string | undefined;
    metadata: TokenListMetadata | undefined;
    wallet: string | undefined;
}) {
    const activity = useLendVaultActivity(
        vaultAddress && wallet
            ? {
                  vaultAddress,
                  wallet,
                  page: 0,
                  pageSize: POSITION_FETCH_SIZE,
                  sortDirection: SortDirection.Desc
              }
            : undefined
    );
    const decimals = metadata?.decimals;
    const rows = useMemo(() => {
        const items = activity.data?.items;
        if (!items?.length || decimals === undefined) return [];
        return calculateActivityContributions(items, true).map((item, index) => ({
            key: `${item.txSignature}-${index}`,
            txSignature: item.txSignature,
            timestamp: item.timestamp,
            actionType: item.actionType,
            amount: toTokenAmount(item.amount, decimals),
            owner: item.owner,
            netDeposits: toTokenAmount(item.contribution, decimals)
        }));
    }, [activity.data?.items, decimals]);
    const columns = useMemo(() => getColumns("position", metadata), [metadata]);
    const loading = activity.isLoading || activity.isFetching;

    return (
        <TransactionHistoryTable rows={rows} columns={columns} loading={loading} pageSize={POSITION_TABLE_PAGE_SIZE} />
    );
}

function TransactionHistoryTable({
    rows,
    columns,
    loading,
    pageSize,
    page,
    setPage,
    totalDataCount
}: {
    rows: VaultTransactionHistoryRow[];
    columns: TableColumn<VaultTransactionHistoryRow>[];
    loading: boolean;
    pageSize: number;
    page?: number;
    setPage?: DispatchType<number>;
    totalDataCount?: number;
}) {
    const showPagination = page !== undefined && !!setPage && !!totalDataCount && totalDataCount > pageSize;
    return (
        <SectionedCard title="Transaction history">
            <Column spacing={2}>
                <Table
                    rows={createRows(rows, (row) => row.key)}
                    columns={columns}
                    loading={loading}
                    emptyPlaceholder="No activity"
                    paginationProps={{ type: "managed", pageSize }}
                />
                {showPagination && (
                    <Pagination count={totalDataCount} rowsPerPage={pageSize} page={page} setPage={setPage} />
                )}
            </Column>
        </SectionedCard>
    );
}

function toActivityRow(item: LendVaultActivityItem, index: number, decimals: number): VaultTransactionHistoryRow {
    return {
        key: `${item.txSignature}-${index}`,
        txSignature: item.txSignature,
        timestamp: item.timestamp,
        actionType: item.actionType,
        amount: toTokenAmount(item.amount, decimals),
        owner: item.owner
    };
}

// Column config keyed by variant. Both variants share Transaction / Time / Activity / Value; Activity
// adds Signer, Position swaps it for Net Deposits. Shaped so Earn's richer Activity/Value cells (#2069)
// can slot in as additional variants without changing callers.
function getColumns(
    variant: VaultTransactionHistoryVariant,
    metadata: TokenListMetadata | undefined
): TableColumn<VaultTransactionHistoryRow>[] {
    const columns: TableColumn<VaultTransactionHistoryRow>[] = [
        {
            dataIndex: "transaction",
            title: "Transaction",
            align: "left",
            render: ({ data }) => <TxnSignature signature={data.txSignature} />
        },
        {
            dataIndex: "time",
            title: "Time",
            render: ({ data }) => <Text>{formatDate(data.timestamp, "date", { showTime: "always" })}</Text>,
            sortBy: variant === "position" ? ({ data }) => data.timestamp : undefined
        },
        {
            dataIndex: "activity",
            title: "Activity",
            render: ({ data }) => <EventTag kind={getVaultActionTagKind(data.actionType)} />
        }
    ];

    if (variant === "activity") {
        columns.push({
            dataIndex: "signer",
            title: "Signer",
            render: ({ data }) => <AddressCopy address={data.owner} variant="body1" />
        });
    }

    columns.push({
        dataIndex: "value",
        title: "Value",
        align: "right",
        render: ({ data }) => <Text>{formatSignedActionAmount(data.amount, data.actionType, metadata)}</Text>
    });

    if (variant === "position") {
        columns.push({
            dataIndex: "netDeposits",
            title: COPY.NET_DEPOSITS,
            align: "right",
            render: ({ data }) => <NetDepositsCell netDeposits={data.netDeposits} metadata={metadata} />
        });
    }

    return columns;
}

function NetDepositsCell({
    netDeposits,
    metadata
}: {
    netDeposits: TokenAmount | undefined;
    metadata: TokenListMetadata | undefined;
}) {
    if (netDeposits === undefined) return <Text>—</Text>;
    // A negative net contribution means the position has returned more than was put in (profit).
    const color = netDeposits.amount < 0n ? "success" : undefined;
    return (
        <Text color={color}>
            {formatToken(netDeposits, { symbol: metadata?.symbol, compactLargeNumbers: true, short: true })}
        </Text>
    );
}

function formatSignedActionAmount(
    amount: TokenAmount,
    actionType: VaultActionType,
    metadata: TokenListMetadata | undefined
): string {
    const signed = actionType === VaultActionType.Withdrawal ? { ...amount, amount: -amount.amount } : amount;
    return formatToken(signed, { symbol: metadata?.symbol, compactLargeNumbers: true, short: true, signed: true });
}

function getVaultActionTagKind(actionType: VaultActionType): EventTagKind {
    return actionType === VaultActionType.Withdrawal ? "withdrawInstant" : "deposit";
}
